<template>
    <div>
        <!-- Hero Section -->
        <v-container
            fluid
            class="hero-section d-flex pa-0"
        >
            <v-col
                cols="12"
                class="d-flex align-center justify-center text-center"
            >
                <div class="hero-content pa-8">
                    <h1 class="display-1 font-weight-bold text-primary mb-4">
                        Effortless Studio Scheduling
                    </h1>
                    <p class="text-h6 text-grey-darken-1 mb-6">
                        Create schedules in minutes instead of days. Keep
                        students happy with times that work for everyone. Teach
                        at your best hours while accommodating student
                        preferences. Studoku finds times that work perfectly for
                        you AND your students—effortlessly.
                    </p>
                    <!-- Primary CTA - conditional based on feature flag -->
                    <v-btn
                        v-if="store.hasFeature('self-signup')"
                        color="primary"
                        size="large"
                        class="mr-4 mb-3"
                        to="/signup"
                    >
                        Sign Up Free
                    </v-btn>
                    <v-btn
                        v-else
                        color="primary"
                        size="large"
                        class="mr-4 mb-3"
                        @click="waitlistModal = true"
                    >
                        Join the Wait List
                    </v-btn>
                    <v-btn
                        variant="outlined"
                        color="primary"
                        size="large"
                        class="mb-3"
                        to="/philosophy"
                    >
                        The Studoku Difference
                    </v-btn>
                </div>
            </v-col>
        </v-container>

        <!-- Features Section -->
        <v-container class="py-12">
            <v-row>
                <v-col cols="12">
                    <h2 class="text-h4 text-center mb-8 text-primary">
                        Why Teachers Choose Studoku
                    </h2>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    v-for="(feature, index) in features"
                    :key="index"
                    cols="12"
                    md="4"
                    class="mb-6"
                >
                    <v-card
                        class="h-100 text-center pa-6"
                        elevation="2"
                        hover
                    >
                        <v-icon
                            :icon="feature.icon"
                            size="64"
                            :color="feature.color"
                            class="mb-4"
                        />
                        <h3 class="text-h6 mb-3">
                            {{ feature.title }}
                        </h3>
                        <p class="text-body-1 text-grey-darken-1">
                            {{ feature.description }}
                        </p>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>

        <!-- How It Works Section -->
        <v-container
            fluid
            class="grey-lighten-5 py-12"
        >
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <h2 class="text-h4 text-center mb-8 text-primary">
                            How It Works
                        </h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col
                        v-for="(step, index) in steps"
                        :key="index"
                        cols="12"
                        md="6"
                        lg="3"
                        class="mb-6"
                    >
                        <div class="text-center">
                            <v-avatar
                                :color="step.color"
                                size="64"
                                class="mb-4"
                            >
                                <span
                                    class="text-h5 text-white font-weight-bold"
                                    >{{ index + 1 }}</span
                                >
                            </v-avatar>
                            <h3 class="text-h6 mb-3">
                                {{ step.title }}
                            </h3>
                            <p class="text-body-1 text-grey-darken-1">
                                {{ step.description }}
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- More Than a Timeslot Section -->
        <v-container class="py-12">
            <v-row
                align="center"
                justify="center"
            >
                <v-col
                    cols="12"
                    md="6"
                    class="pr-md-8"
                >
                    <div class="text-md-left text-center">
                        <h2 class="text-h4 font-weight-bold text-primary mb-4">
                            Better Schedules, Better Studios.
                        </h2>
                        <p
                            class="text-h6 text-grey-darken-1 font-weight-regular mb-6"
                        >
                            Scheduling conflicts are a leading cause of student
                            churn and teacher burnout.
                        </p>
                        <p class="text-body-1 text-grey-darken-1">
                            Studoku builds schedules around the shared
                            preferences of you and your students. The result is
                            a more stable, engaged, and successful studio
                            environment for everyone.
                        </p>
                    </div>
                </v-col>
                <v-col
                    cols="12"
                    md="6"
                >
                    <v-img
                        src="/logo-med.webp"
                        alt="Studoku logo"
                        max-width="300"
                        aspect-ratio="1"
                        class="mx-auto"
                    />
                </v-col>
            </v-row>
        </v-container>

        <!-- CTA Section -->
        <v-container
            fluid
            class="bg-primary py-12"
        >
            <v-container>
                <v-row>
                    <v-col
                        cols="12"
                        class="text-center"
                    >
                        <h2 class="text-h4 text-white font-weight-bold mb-3">
                            Ready to try Studoku?
                        </h2>
                        <!-- Conditional content based on feature flag -->
                        <div v-if="store.hasFeature('self-signup')">
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                Create your account and start building better
                                schedules today.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                aria-label="Sign up for Studoku"
                                to="/signup"
                            >
                                Get Started Free
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Start creating better schedules in minutes.
                            </p>
                        </div>
                        <div v-else>
                            <p
                                class="text-h6 text-white text-opacity-90 font-weight-regular mb-6"
                            >
                                We're launching soon. Join our waitlist and
                                we'll let you know when it's ready.
                            </p>
                            <v-btn
                                color="yellow-darken-2"
                                size="large"
                                class="font-weight-bold mb-3"
                                aria-label="Join the Studoku waitlist"
                                @click="waitlistModal = true"
                            >
                                Join Waitlist
                                <v-icon
                                    end
                                    :icon="mdiArrowRight"
                                />
                            </v-btn>
                            <p class="text-caption text-white text-opacity-70">
                                Just your email. No spam.
                            </p>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-container>

        <!-- Waitlist Dialog Component - only when self-signup is disabled -->
        <WaitlistModal
            v-if="!store.hasFeature('self-signup')"
            v-model="waitlistModal"
        />
    </div>
</template>

<script lang="ts" setup>
import {
    mdiAccountGroup,
    mdiArrowRight,
    mdiClockFast,
    mdiHumanGreetingVariant,
} from '@mdi/js';
import { ref } from 'vue';

import WaitlistModal from '@/shared/components/modals/WaitlistModal.vue';
import { useAppStore } from '@/shared/store';

const store = useAppStore();
const waitlistModal = ref(false);

const features = [
    {
        title: 'Time-Saving Automation',
        description:
            'Create perfect studio schedules in minutes instead of days or weeks. Studoku eliminates manual scheduling headaches.',
        icon: mdiClockFast,
        color: 'blue',
    },
    {
        title: 'Student Retention',
        description:
            "Accommodate your students' scheduling needs. Prevent dropouts due to time conflicts that frustrate families.",
        icon: mdiAccountGroup,
        color: 'green',
    },
    {
        title: 'Teacher Wellbeing',
        description:
            'Teach during your optimal hours. No more sacrificing your evenings, early mornings, or mealtimes.',
        icon: mdiHumanGreetingVariant,
        color: 'orange',
    },
];

const steps = [
    {
        title: 'Set Availability',
        description:
            'Define your teaching hours, considering any constraints to establish the framework for your schedule.',
        color: 'blue',
    },
    {
        title: 'Add Students',
        description:
            'Create your student roster with their lesson lengths and contact information.',
        color: 'green',
    },
    {
        title: 'Collect Preferences',
        description:
            "Gather each student's preferred times and scheduling constraints through our simple survey system.",
        color: 'orange',
    },
    {
        title: 'Generate Schedule',
        description:
            'Let Studoku create an optimized schedule that maximizes satisfaction and minimizes conflicts.',
        color: 'purple',
    },
];
</script>

<style scoped>
.hero-section {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    min-height: 500px;
}

.hero-content {
    max-width: 800px;
}

.text-wrap .v-list-item-title {
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
}
</style>
